"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarUrl } from "@talentir/shared/helpers";
import Image from "next/image";
import { useMemo, useState } from "react";
import { Skeleton } from "./skeleton";

type AvatarData = {
    avatar: string | null;
    username: string | null;
};

export function TalentirAvatar({ data, size = "sm" }: { data?: AvatarData; size?: "xs" | "sm" | "md" | "lg" | "xl" }) {
    const avatarUrl = data?.avatar != null ? getAvatarUrl(data.avatar) : undefined;

    const noAvatarAvailable = data?.avatar === null;

    const [nextImageFinishedLoading, setNextImageFinishedLoading] = useState(false);

    // convert sizes to size in pixel
    const sizeInPixel = useMemo(() => {
        switch (size) {
            case "xs":
                return 24;
            case "sm":
                return 32;
            case "md":
                return 40;
            case "lg":
                return 100;
            case "xl":
                return 160;
        }
    }, [size]);

    const fontSize = useMemo(() => {
        switch (size) {
            case "xs":
                return "text-sm";
            case "sm":
                return "text-base";
            case "md":
                return "text-lg";
            case "lg":
                return "text-6xl";
            case "xl":
                return "text-8xl";
        }
    }, [size]);

    return (
        <div className="relative" style={{ width: sizeInPixel, height: sizeInPixel }}>
            {data != null && (
                <Avatar className="absolute" style={{ width: sizeInPixel, height: sizeInPixel }}>
                    <AvatarImage src={avatarUrl} asChild>
                        {avatarUrl != null && (
                            <Image
                                src={avatarUrl}
                                width={sizeInPixel}
                                height={sizeInPixel}
                                alt={data.username ?? "User"}
                                onLoadingComplete={() => {
                                    setNextImageFinishedLoading(true);
                                }}
                            />
                        )}
                    </AvatarImage>
                    <AvatarFallback className={fontSize}>
                        {data.username != null ? data.username[0] : ""}
                    </AvatarFallback>
                </Avatar>
            )}

            {!nextImageFinishedLoading && !noAvatarAvailable && (
                <>
                    <Skeleton
                        className="absolute z-20 rounded-full"
                        style={{ width: sizeInPixel, height: sizeInPixel }}
                    />
                    <div
                        className="absolute z-10 rounded-full bg-black"
                        style={{ width: sizeInPixel, height: sizeInPixel }}
                    />
                </>
            )}
        </div>
    );
}
